import { allTargetsManaged, type QueryDeploymentSummary } from "~/types";

export const SCALE_ROLES = ["Query", "Index", "Router", "Billing"];

/** A scalable resource: a machine role or a query deployment. */
export interface ScaleResource {
  kind: "role" | "deployment";
  id: string;
  label: string;
}

/**
 * Resources offered by the scale form: the machine roles, then one entry per
 * query deployment whose targets are all operator-managed.
 */
export function scaleResources(
  queryDeployments: Record<string, QueryDeploymentSummary> | null | undefined,
): ScaleResource[] {
  const roles: ScaleResource[] = SCALE_ROLES.map((role) => ({
    kind: "role",
    id: role,
    label: role,
  }));
  const deployments: ScaleResource[] = Object.entries(queryDeployments ?? {})
    .filter(([, summary]) => allTargetsManaged(summary))
    .map(
      ([name]): ScaleResource => ({
        kind: "deployment",
        id: name,
        label: `Query – ${name.replace(/^turbopuffer-query-?/, "") || name}`,
      }),
    )
    .sort((a, b) => a.label.localeCompare(b.label));
  return [...roles, ...deployments];
}

/** One row of the per-target scale table. */
export interface ScaleTargetRow {
  target: string;
  current: number;
}

/** Table rows for a deployment's managed targets. */
export function scaleTargetRows(
  targets: Record<string, number | null>,
): ScaleTargetRow[] {
  return Object.entries(targets)
    .flatMap(([target, replicas]) =>
      replicas === null ? [] : [{ target, current: replicas }],
    )
    .sort((a, b) => a.target.localeCompare(b.target));
}

/**
 * Delta between a desired-replicas input and the current count; null when the
 * current count is unknown or the input isn't a non-negative integer.
 */
export function scaleReplicasDelta(
  current: number | null,
  desired: string,
): number | null {
  if (current === null || desired.trim() === "") {
    return null;
  }
  const parsed = Number(desired);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return null;
  }
  return parsed - current;
}
